import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AssetDto } from '@nusign/picflow-backend-types';
import { catchError, firstValueFrom, from, map, of, switchMap, throwError, timer } from 'rxjs';
import { BoxAuthDialogComponent } from '../account/dialogs/box-auth-dialog/box-auth-dialog.component';
import { BoxPickerWrapperDialogComponent } from '../account/dialogs/box-picker-wrapper-dialog/box-picker-wrapper-dialog.component';
import { loadScript } from '../lib/load-script';
import { WITH_AUTH } from '../network-interceptor';
import { PICFLOW_CONFIG } from '../picflow-config';
import { DataLoaderService } from './data-loader.service';
import { DialogService } from './dialog.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StorageService } from './storage.service';
import { UploadService } from './upload.service';

export interface BoxFile {
  name: string;
  url: string;
  size: number;
  source: 'box';
}

@Injectable({
  providedIn: 'root',
})
export class BoxService {
  private loader = inject(DataLoaderService);
  private observableLoader = inject(ObservableDataLoaderService);
  private storage = inject(StorageService);
  private config = inject(PICFLOW_CONFIG);
  private dialog = inject(DialogService);
  private uploadService = inject(UploadService);
  private http = inject(HttpClient);

  clientId = 'wlgjkgcwf9erm422ufukfjcl0r6qxajt';

  constructor() {
    this.uploadService.alternativeUploadServices.push({
      source: 'box',
      service: this,
    });
  }

  private getRefreshTokenData() {
    const refreshTokenData = this.storage.get<{ refreshToken: string; exp: number }>('box-refresh-token');
    if (!refreshTokenData) return from(this.dialog.open(BoxAuthDialogComponent));
    const isExpired = Date.now() > refreshTokenData.exp;
    if (isExpired) {
      this.storage.remove('box-refresh-token');
      return from(this.dialog.open(BoxAuthDialogComponent));
    }
    return of(refreshTokenData);
  }

  private getAccessTokenFromRefreshToken(refreshToken: string) {
    if (!refreshToken) return of(undefined);
    return this.http
      .post<{
        access_token: string;
        refresh_token: string;
      }>(`${this.config.internalApiUrlBase}/box-access-token/${refreshToken}`, {})
      .pipe(
        map(res => {
          if (!res) {
            this.storage.remove('box-refresh-token');
            return;
          }
          this.storeRefreshToken(res.refresh_token);
          return res.access_token;
        })
      );
  }

  private getAccessTokenInternal() {
    return this.getRefreshTokenData().pipe(
      switchMap((refreshTokenData: { refreshToken: string }) => {
        return this.getAccessTokenFromRefreshToken(refreshTokenData?.refreshToken);
      }),
      switchMap(accessToken => {
        if (!accessToken) return this.getAccessTokenInternal();
        return of(accessToken);
      })
    );
  }

  public getAccessToken() {
    return this.observableLoader.get('box_access_token', () => this.getAccessTokenInternal(), 1000 * 60 * 45, false);
  }

  private loadBox() {
    return this.loader.get('box-filepicker', async () => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn01.boxcdn.net/platform/elements/20.0.0/en-US/picker.css';
      document.head.appendChild(link);
      await loadScript('https://cdn01.boxcdn.net/platform/elements/20.0.0/en-US/picker.js');
      return new window['Box'].FilePicker();
    });
  }

  public async startChooser(): Promise<BoxFile[]> {
    const [accessToken, filePicker] = await Promise.all([firstValueFrom(this.getAccessToken()), this.loadBox()]);
    if (!accessToken) return [];
    const files = <BoxFile[]>await this.dialog.open(BoxPickerWrapperDialogComponent, { filePicker, accessToken });
    return files || [];
  }

  public async uploadFile(
    file: BoxFile,
    galleryId: string,
    sectionId: string,
    position: number,
    assetToReplace?: AssetDto,
    version_name?: string
  ): Promise<AssetDto> {
    return firstValueFrom(
      this.http
        .post<AssetDto>(
          this.config.internalApiUrlBase + '/upload-from-box',
          {
            gallery: galleryId,
            section: sectionId,
            asset_name: file.name,
            position,
            authenticated_download_url: file.url,
            asset_to_replace: assetToReplace?.id,
            version_name,
            box_access_token: await firstValueFrom(this.getAccessToken()),
          },
          { context: new HttpContext().set(WITH_AUTH, true) }
        )
        .pipe(
          catchError(err => {
            if (err instanceof HttpErrorResponse) {
              if (err.status === 503) {
                return timer(10_000).pipe(map(() => undefined));
              }
            }
            return throwError(() => err);
          })
        )
    );
  }

  public getRefreshToken(code: string) {
    return this.http.post<{ access_token: string; refresh_token: string }>(
      `${this.config.internalApiUrlBase}/box-refresh-token/${code}`,
      { code }
    );
  }

  public storeRefreshToken(refreshToken: string) {
    return this.storage.set('box-refresh-token', { refreshToken, exp: Date.now() + 1000 * 60 * 60 * 24 * 59 });
  }
}

// {
//   "type": "file",
//   "id": "1520377187496",
//   "etag": "1",
//   "name": "allec-gomes-cgGMg_PAjM4-unsplash.jpg",
//   "size": 3764901,
//   "parent": {
//       "type": "folder",
//       "id": "0",
//       "sequence_id": null,
//       "etag": null,
//       "name": "All Files"
//   },
//   "extension": "jpg",
//   "permissions": {
//       "can_download": true,
//       "can_preview": true,
//       "can_upload": true,
//       "can_comment": true,
//       "can_rename": true,
//       "can_delete": true,
//       "can_share": true,
//       "can_set_share_access": true,
//       "can_invite_collaborator": true,
//       "can_annotate": true,
//       "can_view_annotations_all": true,
//       "can_view_annotations_self": true,
//       "can_create_annotations": true,
//       "can_view_annotations": true
//   },
//   "path_collection": {
//       "total_count": 1,
//       "entries": [
//           {
//               "type": "folder",
//               "id": "0",
//               "sequence_id": null,
//               "etag": null,
//               "name": "All Files"
//           }
//       ]
//   },
//   "modified_at": "2024-05-03T07:17:48-07:00",
//   "created_at": "2024-05-03T07:17:48-07:00",
//   "modified_by": {
//       "type": "user",
//       "id": "33594172223",
//       "name": "Nathan Felix",
//       "login": "nathan@nusign.com"
//   },
//   "has_collaborations": false,
//   "is_externally_owned": false,
//   "authenticated_download_url": "https://public.boxcloud.com/api/2.0/files/1520377187496/content",
//   "is_download_available": true,
//   "representations": {
//       "entries": [
//           {
//               "representation": "jpg",
//               "properties": {
//                   "dimensions": "1024x1024",
//                   "paged": "false",
//                   "thumb": "false"
//               },
//               "info": {
//                   "url": "https://api.box.com/2.0/internal_files/1520377187496/versions/1669635028296/representations/jpg_1024x1024"
//               },
//               "status": {
//                   "state": "success"
//               },
//               "content": {
//                   "url_template": "https://public.boxcloud.com/api/2.0/internal_files/1520377187496/versions/1669635028296/representations/jpg_1024x1024/content/{+asset_path}"
//               }
//           }
//       ]
//   },
//   "allowed_shared_link_access_levels": [
//       "collaborators",
//       "open"
//   ],
//   "shared_link": {
//       "url": "https://app.box.com/s/yko3fhjetukbijzvm68qsgq5j7mrvdf5",
//       "download_url": "https://app.box.com/shared/static/yko3fhjetukbijzvm68qsgq5j7mrvdf5.jpg",
//       "vanity_url": null,
//       "vanity_name": null,
//       "effective_access": "open",
//       "effective_permission": "can_download",
//       "is_password_enabled": false,
//       "unshared_at": null,
//       "download_count": 0,
//       "preview_count": 0,
//       "access": "open",
//       "permissions": {
//           "can_preview": true,
//           "can_download": true,
//           "can_edit": false
//       }
//   }
// }
